import { CHECK_SEVERITY_LEVELS, SEVERITY_LEVEL_TEXT } from '@/components/Agents/SendValidator/utils/SendSeverity';
import { countOf, formatCount } from '@/components/Agents/SendValidator/utils/SendText';
import { _isNil, _isNotEmpty } from '@/littledash';
import type { SendSeverityCounts } from '@/model/Agent.model';
import type { FC, ReactNode } from 'react';
import { severityBadgeClass, severityMarkClass } from './SendSeverityStyles';
import styles from './SendResults.module.scss';

interface SendSeverityCountPillsProps {
  /** The server's counts of a section's check findings by level; absent on a result stored before them. */
  counts?: SendSeverityCounts;
  testId?: string;
}

/**
 * A section's findings by severity as compact pills: one per level the checks found something at,
 * carrying the server's own count for it. The counts already leave out Benchling In Vivo AI notes and
 * a check's notices that it could not decide, so neither is ever a pill. A result stored before the
 * counts existed shows none rather than counts worked out from the findings it carries.
 *
 * Each pill has its level's mark shape beside the number, and its words for assistive tech and on
 * hover, so no level is told apart by colour alone.
 */
const SendSeverityCountPills: FC<SendSeverityCountPillsProps> = ({ counts, testId = 'send-severity-counts' }) => {
  if (_isNil(counts)) {
    return null;
  }
  const pills = CHECK_SEVERITY_LEVELS.reduce<Array<ReactNode>>((shown, level) => {
    const count = counts[level];
    if (count <= 0) {
      return shown;
    }
    const word = SEVERITY_LEVEL_TEXT[level].toLowerCase();
    const words = countOf(count, `${word} finding`, `${word} findings`);
    shown.push(
      <span
        key={level}
        className={`${styles.badge} ${styles.countPill} ${severityBadgeClass[level]}`}
        title={words}
        data-test-element="severity-count"
        data-test-key={level}
      >
        <span className={`${styles.severityMark} ${severityMarkClass[level]}`} aria-hidden="true" />
        <span aria-hidden="true">{formatCount(count)}</span>
        <span className="clip">{words}</span>
      </span>
    );
    return shown;
  }, []);
  if (!_isNotEmpty(pills)) {
    return null;
  }
  return (
    <span className={styles.countPills} data-test-component="SendSeverityCountPills" data-testid={testId}>
      {pills}
    </span>
  );
};

export default SendSeverityCountPills;
